import {IconRotateClockwise2, IconUsers} from '@tabler/icons-react';
import type {MRT_RowSelectionState} from 'mantine-react-table';
import React, {useRef, useState} from 'react';
import type {PersonnelSimpleResponse} from 'src/api/ProactApiClient';
import {UserStatusEnum, UserTypeEnum} from 'src/api/ProactApiClient';
import {useBulkActivateUserMutation} from 'src/redux/queries/personnel';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {VendorDocumentTitle} from 'src/pages/Modules/Vendors/shared/vendorHelpers';
import {usePersonnelPermissions} from 'src/shared/auth/permissions/usePersonnelPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {VendorPersonnelTableRef} from '../../VendorView/components/VendorPersonnelTable/VendorPersonnelTable';
import {VendorPersonnelTable} from '../../VendorView/components/VendorPersonnelTable/VendorPersonnelTable';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const PendingContractorsList: React.FC = () => {
  useSetDocumentTitle(VendorDocumentTitle);
  const {setAlert} = useAlert();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const navigate = useAppNavigate();
  const personnelPerms = usePersonnelPermissions();
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const isRowsSelected = Object.keys(rowSelection ?? {}).length > 0;
  const tableRef = useRef<VendorPersonnelTableRef>(null);
  const [bulkActivateUser] = useBulkActivateUserMutation();

  const handleActivateUsers = async () => {
    try {
      if (!isRowsSelected) {
        return;
      }

      setIsUpdating(true);
      const ids = Object.keys(rowSelection).map(id => +id);

      const res = await bulkActivateUser({ids}).unwrap();

      setRowSelection({});

      if (res) {
        setAlert('Selected users have been activated.', 'success');
        tableRef.current?.refetch();
      } else {
        setAlert(
          'An issue was encountered while activating users. Some users may not have been activated. Please refresh to confirm.',
          'error'
        );
      }
    } catch (error: unknown) {
      setAlert(parseErrorMessage(error), 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRowClick = (row: PersonnelSimpleResponse) => {
    navigate(
      PathUtility.VendorPendingPersonnelView(row.id, row.vendorId!, 'overview')
    );
  };

  const actionBarPreNode = (
    <>
      {personnelPerms.auth && (
        <TableToolbar.Action
          disabled={!isRowsSelected}
          loading={isUpdating}
          variant="primary"
          onClick={handleActivateUsers}
          icon={<IconRotateClockwise2 />}>
          Activate Users
        </TableToolbar.Action>
      )}
    </>
  );

  return (
    <>
      <PageHeader>
        <PageHeader.Title
          icon={<IconUsers />}
          subtitle="Contractors awaiting activation">
          Pending Review
        </PageHeader.Title>
      </PageHeader>
      <VendorPersonnelTable
        ref={tableRef}
        identifier="pending-contractors"
        userType={UserTypeEnum.Contractor}
        defaultColumnFilters={[
          {
            id: 'active',
            value: [UserStatusEnum.Pending.toString()],
          },
        ]}
        defaultSorting={[{id: 'createdAt', desc: true}]}
        defaultColumnVisibility={{
          jobCode: false,
          'vendor.id': false,
          updatedAt: false,
          isSuspended: false,
          isLeaveOfAbsence: false,
          isEligible: false,
          isBannedOnProperty: false,
        }}
        enableRowSelection={personnelPerms.auth}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        onRowClick={handleRowClick}
        actionBarPreNode={actionBarPreNode}
        disableActiveFilter={true}
        disablePendingActionFilter={true}
      />
    </>
  );
};
